import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * FAQ Component
 * Left side: "FREQUENT QUESTIONS" title, description, Discord button
 * Right side: Collapsible accordion with 6 FAQ items
 * Desktop: Absolute positioned layout within 1024px scaler
 * Mobile: Stubbed for future implementation
 */

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'What is a Playcast Host?',
    answer:
      'A Host is someone who shares their PC with the Playcast network and earns rewards in return.',
  },
  {
    question: 'How do hosts benefit Playcast?',
    answer:
      "Hosts dramatically reduce Playcast's costs, deliver far better performance than low-powered cloud servers, and provide smoother gameplay through lower latency. By contributing their powerful gaming PCs, Hosts aren't just improving the network — they're helping power a revolution in how games are played and streamed.",
  },
  {
    question: 'Is my data safe?',
    answer:
      'Yes. When Playcast uses your PC during idle time to run interactive demos, this is called Host Mode, where all sessions run inside a separate, secure Windows account — meaning players can never access your desktop, files, or personal data.',
  },
  {
    question: 'Do I need to leave Playcast open 24/7?',
    answer:
      "Nope — Playcast runs as a background service. There's no need to have the app open to host. As of right now, we are in a recruitment period: active hosting is not rolled out yet. When hosting is rolled out, Playcast will host only during your idle PC times.",
  },
  {
    question: 'Do I get access to free games as a Host?',
    answer:
      "Yes! We're adding more rewards all the time — open the Playcast desktop app to check out the current catalog.",
  },
  {
    question: 'Where can I contact Playcast Support?',
    answer:
      "Join our Discord and share your issue as clearly and specifically as possible in the support channel — the Playcast team will help as soon as we're available. If your concern involves private information, such as personal rewards or sensitive bug details, please open a ticket instead. This creates a private channel between you and the Playcast team where we can assist you securely.",
  },
];

const DISCORD_URL = 'http://discord.gg/playcast';

const WHITE = 'rgba(255, 255, 255, 1)';
const ANSWER_COLOR = '#ccc';
const DIVIDER_COLOR = 'rgba(255, 255, 255, 0.5)';
const TRANSITION_SPEED = '0.35s';

const PlusIcon = ({ isOpen }: { isOpen: boolean }) => (
  <span
    style={{
      position: 'relative',
      width: '20px',
      height: '20px',
      flexShrink: 0,
      display: 'inline-block',
      transition: `transform ${TRANSITION_SPEED} ease`,
      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
    }}
  >
    <span
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '14px',
        height: '2px',
        background: WHITE,
      }}
    />
    <span
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '2px',
        height: '14px',
        background: WHITE,
      }}
    />
  </span>
);

interface FaqAccordionItemProps {
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

const FaqAccordionItem = ({
  item,
  index,
  isOpen,
  onToggle,
}: FaqAccordionItemProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, item.answer]);

  return (
    <div
      style={{
        borderTop: index > 0 ? `1px solid ${DIVIDER_COLOR}` : 'none',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: `FAQ: ${item.question}`, buttonLocation: 'FAQ' }); onToggle(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: '16px',
            color: WHITE,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {item.question}
        </span>
        <PlusIcon isOpen={isOpen} />
      </div>

      <div
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
          overflow: 'hidden',
          transition: `max-height ${TRANSITION_SPEED} ease`,
        }}
      >
        <div
          ref={contentRef}
          style={{
            padding: '10px 20px 16px',
          }}
        >
          <p
            style={{
              color: ANSWER_COLOR,
              fontSize: '16px',
              lineHeight: '1.5',
              fontWeight: 350,
              fontFamily: 'Inter, sans-serif',
              margin: 0,
            }}
          >
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

export const FAQ = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = useCallback(
    (index: number) => {
      setOpenIndex((prev) => (prev === index ? -1 : index));
    },
    []
  );

  return (
    <>
      {/* Desktop Layout — two-column flex so accordion drives section height */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: '#000000',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{
            display: 'flex',
            width: '100%',
            minHeight: '300px',
            paddingLeft: '123px',
            paddingTop: '24px',
            paddingBottom: '24px',
            boxSizing: 'border-box',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Left Column — title, description, button */}
          <div
            style={{
              width: '325px',
              flexShrink: 0,
              paddingTop: '0px',
            }}
          >
            <h1
              style={{
                lineHeight: '59px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '48px',
                color: WHITE,
                margin: 0,
                fontVariationSettings: "'wght' 900",
              }}
            >
              FREQUENT
            </h1>
            <h1
              style={{
                lineHeight: '29px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '48px',
                color: WHITE,
                margin: 0,
                fontVariationSettings: "'wght' 900",
              }}
            >
              QUESTIONS
            </h1>

            <div style={{ marginTop: '29px', width: '275px' }}>
              <p
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '12px',
                  color: WHITE,
                  margin: 0,
                  lineHeight: '1.5',
                }}
              >
                Discover answers to the most frequently asked questions about
                Playcast, explore our innovative gaming platform, and see how
                we&apos;re transforming the way you play.
              </p>
            </div>

            <div style={{ marginTop: '30px' }}>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
                onClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Discord', buttonLocation: 'FAQ' })}
              >
                <button
                  className="aida-button"
                  style={{
                    width: '134px',
                    height: '26px',
                    borderRadius: '30px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '26px',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    className="text"
                    style={{ height: '26px', lineHeight: '26px' }}
                  >
                    Join the Discord
                  </span>
                </button>
              </a>
            </div>
          </div>

          {/* Right Column — Accordion (in flow, drives section height) */}
          <div
            style={{
              width: '461px',
              flexShrink: 0,
              borderRadius: '20px',
              boxSizing: 'border-box',
              padding: '0px 24px',
            }}
          >
            {FAQ_ITEMS.map((item, i) => (
              <FaqAccordionItem
                key={i}
                item={item}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout — stacked: title, accordion, discord button */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: '#000000',
          overflow: 'visible',
          padding: '40px 20px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Title */}
        <div style={{ textAlign: 'center' }}>
          <h1
            style={{
              lineHeight: '50px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '40px',
              color: WHITE,
              margin: 0,
              fontVariationSettings: "'wght' 900",
            }}
          >
            FREQUENT
          </h1>
          <h1
            style={{
              lineHeight: '28px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: '40px',
              color: WHITE,
              margin: 0,
              fontVariationSettings: "'wght' 900",
            }}
          >
            QUESTIONS
          </h1>
        </div>

        {/* Accordion */}
        <div
          style={{
            width: '100%',
            maxWidth: '340px',
            marginTop: '24px',
          }}
        >
          {FAQ_ITEMS.map((item, i) => (
            <FaqAccordionItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => handleToggle(i)}
            />
          ))}
        </div>

        {/* Discord Button */}
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', marginTop: '24px' }}
          onClick={() => sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Join the Discord', buttonLocation: 'FAQ' })}
        >
          <button
            className="aida-button"
            style={{
              width: '160px',
              height: '30px',
              borderRadius: '30px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '30px',
              cursor: 'pointer',
            }}
          >
            <span
              className="text"
              style={{ height: '30px', lineHeight: '30px' }}
            >
              Join the Discord
            </span>
          </button>
        </a>
      </motion.section>
    </>
  );
};
